import type { ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, ViewChild, inject } from '@angular/core';

import type { Node as ProsemirrorNode } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { ChecklistEvents } from '@core/prosemirror-legacy';
import {
  ChecklistItemNode,
  ChecklistPluginChecklistChange,
  CheckboxStyles,
  ChecklistItemStyles,
} from '@core/prosemirror-legacy';
import type { ProsemirrorAngularComponent, WysiwygContext } from '@core/prosemirror-legacy/angular';
import { WysiwygContext$ } from '@core/prosemirror-legacy/angular';
import { RtTooltip } from '@core/user-interface-legacy';

@Component({
  templateUrl: 'checklist-item.component.html',
  imports: [RtTooltip],
})
export class ChecklistItemComponent implements ProsemirrorAngularComponent, OnInit, OnDestroy {
  private readonly i18Service = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly wysiwygContext$ = inject(WysiwygContext$, { optional: true });

  public canToggle: boolean;
  public checked: boolean;
  public checklistItemStyles: Partial<CSSStyleDeclaration>;
  public checkboxContainerStyles: Partial<CSSStyleDeclaration> =
    CheckboxStyles.getContainerStyles();
  public checkboxStyles: Partial<CSSStyleDeclaration>;
  public tooltipMessage: string | null = null;
  public pmOnDestroy$ = new Subject<void>();

  @ViewChild('dom', { static: true }) public dom: ElementRef<HTMLElement>;
  @ViewChild('content', { static: true }) public contentDom: ElementRef<HTMLElement>;

  private node: ProsemirrorNode;
  private view: EditorView;
  private getPos: () => number;
  private listEvents$: Subject<ChecklistEvents>;
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit() {
    if (this.wysiwygContext$) {
      this.wysiwygContext$
        .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
        .subscribe((context: WysiwygContext) => {
          this.tooltipMessage = context.owner
            ? this.i18Service.translate('wysiwyg.checklist_disabled_tooltip', {
                name: context.owner.name,
              })
            : null;
          this.cdRef.detectChanges();
        });
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getNodeView(
    node: ProsemirrorNode,
    view: EditorView,
    getPos: () => number,
    canToggle$: Observable<boolean>,
    listEvents$: Subject<ChecklistEvents>,
  ): NodeView {
    this.view = view;
    this.getPos = getPos;
    this.listEvents$ = listEvents$;
    this.update(node);

    canToggle$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(canToggle => {
      this.canToggle = canToggle;
      this.cdRef.detectChanges();
    });

    return {
      dom: this.dom.nativeElement,
      contentDOM: this.contentDom.nativeElement,
      update: (node: ProsemirrorNode) => this.updateView(node),
      destroy: () => this.pmOnDestroy$.next(),
    };
  }

  public onToggle(event: MouseEvent) {
    event.preventDefault();
    if (!this.canToggle) return;

    const pos = this.getPos();
    const {
      dispatch,
      state: { tr },
    } = this.view;
    tr.setNodeMarkup(pos, undefined, { checked: !ChecklistItemNode.isChecked(this.node) });
    dispatch(tr);
    this.listEvents$.next(
      new ChecklistPluginChecklistChange(<{ doc: object }>this.view.state.toJSON()),
    );
  }

  private updateView(node: ProsemirrorNode) {
    if (ChecklistItemNode.is(node)) {
      this.update(node);
      return true;
    }

    return false;
  }

  private update(node: ProsemirrorNode) {
    this.node = node;
    this.checked = ChecklistItemNode.isChecked(node);
    this.calcStyles();
    this.cdRef.detectChanges();
  }

  private calcStyles() {
    if (this.checked) {
      this.checklistItemStyles = {
        ...ChecklistItemStyles.base,
        ...ChecklistItemStyles.checked,
      };
      this.checkboxStyles = {
        ...CheckboxStyles.base,
        ...CheckboxStyles.getCheckedStyles(),
      };
    } else {
      this.checklistItemStyles = {
        ...ChecklistItemStyles.base,
        ...ChecklistItemStyles.notChecked,
      };
      this.checkboxStyles = {
        ...CheckboxStyles.base,
        ...CheckboxStyles.notChecked,
      };
    }
  }
}
